import React, { useCallback, useEffect, useState } from 'react';
import { Category } from '../../../../model/response';
import { TableParams } from '../../../../model/request';
import { Checkbox, message, Popconfirm, Table, TableProps } from 'antd';
import Search from 'antd/es/input/Search';
import { deleteCategory, getPageCategory } from '../../../../service/categoryService';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import processError from '../../../../utils/processError';

function CategoryManagement() {
  const [fetching, setFetching] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const navigate = useNavigate();

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      defaultPageSize: 10,
    },
  });

  const handleCategoryPage: TableProps['onChange'] = (pagination, filters) => {
    setTableParams({
      pagination,
      filters,
    });
  };

  const fetchData = useCallback(() => {
    setFetching(true);
    getPageCategory(tableParams, keyword)
      .then((categoryPage) => {
        setCategories(categoryPage.content);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: categoryPage.totalElements,
          },
        });
      })
      .catch((error) => console.error(error))
      .finally(() => setFetching(false));
  }, [keyword, tableParams]);

  useEffect(() => {
    if (tableParams.pagination) {
      setTableParams({
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
        filters: tableParams.filters,
      });
    }
    fetchData();
  }, [tableParams.pagination?.pageSize, keyword]);

  useEffect(() => {
    fetchData();
  }, [tableParams.pagination?.current]);

  const categoryTableColumns: TableProps<Category>['columns'] = [
    {
      title: 'STT',
      dataIndex: 'key',
      render: (_, category, index) => <div>{index + 1}</div>,
    },
    {
      title: 'Tên chủ đề',
      key: 'name',
      render: (_, category) => <div className={'font-bold text-0B7BFF'}>{category.name}</div>,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, category) => <Checkbox checked={category.webViewActive} />,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
    },
    {
      title: 'Hành động',
      key: 'doTest',
      align: 'center',
      render: (_, category) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handleNavigateUpdateCategory(category)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa chủ đề"
            description="Xác nhận xóa chủ đề. Hành động không thể hoàn tác?"
            onConfirm={() => handleDeleteCategory(category)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined className={'text-white'} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleDeleteCategory = async (category: Category) => {
    try {
      await deleteCategory(category.id);
      fetchData();
      message.success('Xoá chủ đề thành công');
    } catch (err) {
      processError(err);
    }
  };

  function handleNavigateUpdateCategory(category: Category) {
    navigate(`/learning-assets/categories/update/${category.id}`);
  }

  function handleNavigateAddCategory() {
    navigate('/learning-assets/categories/create');
  }

  return (
    <div className={'w-full h-full p-4'}>
      <div className={'p-2 flex items-center justify-between bg-white rounded'}>
        <div className={'flex gap-4'}>
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={handleNavigateAddCategory}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Search
          placeholder={'Nhập từ khóa tìm kiếm'}
          rootClassName={'h-9 rounded-3xl border border-solid border-f46055 flex items-center overflow-hidden w-fit'}
          onSearch={setKeyword}
        />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          dataSource={categories}
          columns={categoryTableColumns}
          rowKey={(category) => category.id}
          onChange={handleCategoryPage}
          loading={fetching}
          pagination={tableParams.pagination}
        />
      </div>
    </div>
  );
}

export default CategoryManagement;
